import {
  Divider,
  Grid2,
  IconButton,
  TableContainer,
  TablePagination,
} from "@mui/material";
import {
  DataGridPro,
  DataGridProProps,
  GridToolbarContainer,
  GridToolbarQuickFilter,
  useGridApiContext,
} from "@mui/x-data-grid-pro";
import { perPagePaginationArray } from "interfaces/FilterTypes";
import { useState } from "react";
import { useAppSelector } from "store/store";
import { paletteLightTheme } from "utils/ColorTheme";
import { CustomLoader, EmptyData } from "./DatagridUtils";
import ExportCsv from "./ExportCsv";
import { AddColumnIcon, Down1Icon, SearchIcon } from "./Icons";
import { Show } from "./Show";
import TableColumnModal from "./TableColumnModal";

interface Props {
  headerComponent?: any;
  headerGridStyle?: any;
  showSearch?: boolean;
  columnsButton?: boolean;
  densityButton?: boolean;
  exportButton?: boolean;
  ref?: any;
  maxHeight?: any;
  showHeaderGrid?: any;
  handleSortChange?: any;
  rightPinnes?: any;
  initialPageSize?: any;
  minHeight?: any;
  headerTitle?: any;
  emptyButtons?: any;
  footerComponent?: any;
  name: string;
  exportFile?: {
    path?: string;
    exportName?: string;
    hideType?: string[];
  };
  extraComponent?: any;
  disabledColumns?: string[];
}

const GKClientDataGrid = (props: DataGridProProps & Props) => {
  const columnsPersist = useAppSelector(
    (state) => state?.table?.columnsPersist,
  );

  const {
    headerComponent,
    showSearch = true,
    columnsButton = true,
    maxHeight,
    showHeaderGrid = true,
    pagination = true,
    pinnedColumns,
    handleSortChange,
    onRowClick,
    initialPageSize,
    rightPinnes,
    minHeight,
    columns,
    headerTitle,
    name,
    emptyButtons,
    footerComponent,
    initialState,
    exportFile,
    sx,
    rows,
    loading,
    extraComponent,
    disabledColumns,
    exportFile: { hideType = [] } = {},
  } = props;

  // const {
  //   apiRef,
  //   columns: computedColumns,
  //   onColumnOrderChange,
  //   onColumnWidthChange,
  //   onColumnCellDoubleClickWidthChange,
  // } = useColumnsState({
  //   columns,
  //   name,
  // });

  const [tableColumnModalOpen, setTableColumnModalOpen] = useState(false);

  const Pagination = (props: any) => {
    const apiRef = useGridApiContext();

    const handlePageChange = (
      event: React.MouseEvent<HTMLButtonElement> | null,
      newPage: number,
    ) => {
      apiRef?.current?.setPage(newPage);
    };

    const handleRowsPerPageChange = (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      const newRowsPerPage = parseInt(event.target.value, 10);
      apiRef?.current?.setPageSize(newRowsPerPage);
      apiRef.current.setPage(0);
    };

    return pagination ? (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderTop: `solid 1px ${paletteLightTheme.palette.border.main}`,
        }}
      >
        {footerComponent || <div />}
        <TablePagination
          {...props}
          component="div"
          labelRowsPerPage="Rows"
          count={apiRef?.current.getRowsCount()}
          page={apiRef?.current.state.pagination.paginationModel.page}
          rowsPerPage={
            apiRef?.current.state.pagination.paginationModel.pageSize
          }
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={initialPageSize ? [] : perPagePaginationArray}
        />
      </div>
    ) : (
      <div> </div>
    );
  };

  const exportCsv = () => {
    return (
      <ExportCsv
        path={exportFile?.path}
        name={exportFile?.exportName}
        hideType={hideType}
      />
    );
  };

  const CustomToolbar = () => {
    return (
      <GridToolbarContainer style={{ padding: 0 }}>
        <Grid2 container width={`-webkit-fill-available`}>
          <Grid2
            size={12}
            justifyContent={"space-between"}
            alignItems={"center"}
            width={`-webkit-fill-available`}
            p={"12px"}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                width: "100%",
              }}
            >
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <Show.When isTrue={headerTitle}>{headerTitle}</Show.When>
                <Show.When isTrue={showSearch}>
                  <GridToolbarQuickFilter
                    debounceMs={200}
                    variant="outlined"
                    size="small"
                    quickFilterParser={(searchInput) =>
                      searchInput.split(",").map((value) => value.trim())
                    }
                    quickFilterFormatter={(quickFilterValues) =>
                      quickFilterValues.join(", ")
                    }
                    slotProps={{
                      input: {
                        sx: {
                          borderRadius: 2,
                        },
                        startAdornment: (
                          <SearchIcon width={20} color={"var(--grey-500)"} />
                        ),
                      },
                    }}
                  />
                </Show.When>
                <Show.When isTrue={!!exportFile?.path}>{exportCsv()}</Show.When>
                <Show.When isTrue={columnsButton}>
                  <IconButton
                    onClick={() => {
                      setTableColumnModalOpen(true);
                    }}
                  >
                    <AddColumnIcon />
                  </IconButton>
                </Show.When>
              </span>
              <Show.When isTrue={headerComponent}>{headerComponent}</Show.When>
            </div>
          </Grid2>
          <Show.When isTrue={!!extraComponent}>
            <Grid2 size={12}>
              <Divider />
            </Grid2>
            <Grid2
              container
              alignItems={"center"}
              width={`-webkit-fill-available`}
              p={"12px"}
            >
              {extraComponent}
            </Grid2>
          </Show.When>
        </Grid2>
      </GridToolbarContainer>
    );
  };

  return (
    <>
      <TableContainer
        sx={{
          "&.MuiTableContainer-root .MuiDataGrid-toolbarContainer": {
            margin: 0,
            p: 1,
          },
        }}
      >
        <DataGridPro
          {...props}
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          rows={loading ? [] : rows || []}
          onSortModelChange={handleSortChange}
          getDetailPanelHeight={() => "auto"}
          pagination={pagination}
          columnVisibilityModel={
            columnsPersist && name && (columnsPersist[name] as any)
          }
          slots={{
            footer: Pagination,
            loadingOverlay: CustomLoader,
            toolbar: showHeaderGrid ? CustomToolbar : null,
            detailPanelExpandIcon: () => <Down1Icon width={20} height={20} />,
            detailPanelCollapseIcon: () => (
              <Down1Icon
                style={{
                  transform: " rotate(180deg)",
                }}
                width={20}
                height={20}
              />
            ),
            noRowsOverlay: () => <EmptyData emptyButtons={emptyButtons} />,
          }}
          slotProps={{ toolbar: { showQuickFilter: true } }}
          initialState={{
            ...initialState,
            pagination: {
              paginationModel: {
                pageSize: initialPageSize || 25,
              },
            },
          }}
          // columns={computedColumns}
          // onColumnOrderChange={onColumnOrderChange}
          // onColumnResize={(params: GridColumnResizeParams) => {
          //   onColumnWidthChange(params);
          // }}
          // onColumnHeaderDoubleClick={(params: any) => {
          //   if (params.colDef.minWidth !== params.colDef.computedWidth) {
          //     onColumnCellDoubleClickWidthChange({
          //       colDef: params,
          //       width: params.colDef.minWidth,
          //     });
          //   }
          // }}
          pinnedColumns={{
            ...pinnedColumns,
            right: rightPinnes || ["action"],
          }}
          sx={{
            ...sx,
            "& .MuiDataGrid-virtualScroller": {
              maxHeight: maxHeight || "calc(100vh - 256px)",
              minHeight: minHeight || "",
            },
            "&.MuiDataGrid-root .MuiTablePagination-root": {
              padding: "0 8px",
            },

            "& .MuiDataGrid-row": {
              "&:hover": {
                cursor: onRowClick ? "pointer" : "",
              },
            },
          }}
        />
      </TableContainer>

      <Show.When isTrue={tableColumnModalOpen}>
        <TableColumnModal
          open={tableColumnModalOpen}
          setOpen={setTableColumnModalOpen}
          columns={columns}
          columnVisibilityModel={columnsPersist}
          name={name}
          disabledColumns={disabledColumns}
        />
      </Show.When>
    </>
  );
};

export default GKClientDataGrid;
